import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "sonner";
import "@/App.css";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import JobEditor from "@/pages/JobEditor";
import Billing from "@/pages/Billing";
import AuthCallback from "@/pages/AuthCallback";

const isSubscribed = (u) => {
  if (!u) return false;
  if (u.subscription_status === "active" || u.subscription_tier === "lifetime") return true;
  // Trialing users get full access until their trial_ends_at timestamp passes.
  if (u.subscription_status === "trialing" && u.trial_ends_at) {
    return new Date(u.trial_ends_at).getTime() > Date.now();
  }
  return false;
};

const Private = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 font-mono text-sm">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

// Requires the user to have an active subscription (or lifetime unlock).
// If they don't, we route them to /billing to pick a plan.
const Subscribed = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 font-mono text-sm">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!isSubscribed(user)) return <Navigate to="/billing" replace />;
  return children;
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <Toaster position="top-right" richColors closeButton />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/jobs" element={<Private><Dashboard /></Private>} />
            <Route path="/jobs/:jobId" element={<Private><JobEditor /></Private>} />
            <Route path="/billing" element={<Private><Billing /></Private>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
